import { useState, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { useMutation } from '@apollo/client/react';
import { useClientAuth } from '../hooks/useClientAuth';
import { UPDATE_USER } from '../graphql/clientMutations';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import type { Address } from '../types';

interface UpdateUserResponse {
  updateUser: {
    id: string;
    phoneNumber?: string;
    address?: Address;
  };
}

interface UpdateUserVariables {
  id: string;
  input: {
    phoneNumber?: string;
    address?: {
      street1: string;
      street2?: string;
      city: string;
      state: string;
      zipCode: string;
    };
  };
}

/**
 * ProfileScreen - Client profile view and edit
 * 
 * Displays:
 * - Name, email, phone, address, role
 * - Edit functionality for phone number and address
 * - Input validation with success/error messages
 * - Sign out button
 */
export function ProfileScreen() {
  const navigate = useNavigate();
  const { user, signOut, refresh } = useClientAuth();
  const [isEditing, setIsEditing] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [message, setMessage] = useState<{ type: 'success' | 'error'; text: string } | null>(null);

  // Form state
  const [phoneNumber, setPhoneNumber] = useState(user?.phoneNumber || '');
  const [street1, setStreet1] = useState(user?.address?.street1 || '');
  const [street2, setStreet2] = useState(user?.address?.street2 || '');
  const [city, setCity] = useState(user?.address?.city || '');
  const [state, setState] = useState(user?.address?.state || '');
  const [zipCode, setZipCode] = useState(user?.address?.zipCode || '');

  const [updateUser] = useMutation<UpdateUserResponse, UpdateUserVariables>(UPDATE_USER);

  // Format role for display
  const formatRole = (role: string) => {
    switch (role) {
      case 'CLIENT': return 'Client';
      case 'PROSPECT': return 'Potential Client';
      default: return role;
    }
  };

  // Format phone number for display
  const formatPhoneNumber = (phone: string | undefined) => {
    if (!phone) return 'Not provided';
    const cleaned = phone.replace(/\D/g, '');
    if (cleaned.length === 10) {
      return `(${cleaned.slice(0, 3)}) ${cleaned.slice(3, 6)}-${cleaned.slice(6)}`;
    }
    if (cleaned.length === 11 && cleaned.startsWith('1')) {
      return `(${cleaned.slice(1, 4)}) ${cleaned.slice(4, 7)}-${cleaned.slice(7)}`;
    }
    return phone;
  };

  // Validate phone number
  const validatePhoneNumber = (phone: string): boolean => {
    if (!phone) return true;
    const cleaned = phone.replace(/\D/g, '');
    return cleaned.length === 10 || (cleaned.length === 11 && cleaned.startsWith('1'));
  };

  // Validate zip code
  const validateZipCode = (zip: string): boolean => {
    if (!zip) return true;
    return /^\d{5}(-\d{4})?$/.test(zip);
  };

  const handleStartEdit = () => {
    setPhoneNumber(user?.phoneNumber || '');
    setStreet1(user?.address?.street1 || '');
    setStreet2(user?.address?.street2 || '');
    setCity(user?.address?.city || '');
    setState(user?.address?.state || '');
    setZipCode(user?.address?.zipCode || '');
    setIsEditing(true);
    setMessage(null);
  };

  const handleCancelEdit = () => {
    setIsEditing(false);
    setMessage(null);
  };

  const handleSave = useCallback(async () => {
    if (!validatePhoneNumber(phoneNumber)) {
      setMessage({ type: 'error', text: 'Please enter a valid phone number (10 digits)' });
      return;
    }

    if (zipCode && !validateZipCode(zipCode)) {
      setMessage({ type: 'error', text: 'Please enter a valid ZIP code (e.g., 12345 or 12345-6789)' });
      return;
    }

    const hasAddressFields = street1 || street2 || city || state || zipCode;
    if (hasAddressFields && (!street1 || !city || !state || !zipCode)) {
      setMessage({ type: 'error', text: 'Please complete all required address fields (Street, City, State, ZIP)' });
      return;
    }

    setIsSaving(true);
    setMessage(null);

    try {
      const input: UpdateUserVariables['input'] = {};

      const cleanedPhone = phoneNumber.replace(/\D/g, '');
      if (cleanedPhone) {
        input.phoneNumber = cleanedPhone;
      }

      if (hasAddressFields) {
        input.address = {
          street1,
          street2: street2 || undefined,
          city,
          state,
          zipCode,
        };
      }

      await updateUser({ 
        variables: { 
          id: user!.id,
          input 
        } 
      });
      await refresh();
      
      setMessage({ type: 'success', text: 'Profile updated successfully' });
      setIsEditing(false);
    } catch (error: unknown) {
      const errorMessage = error instanceof Error ? error.message : 'Failed to update profile';
      setMessage({ type: 'error', text: errorMessage });
    } finally {
      setIsSaving(false);
    }
  }, [phoneNumber, street1, street2, city, state, zipCode, updateUser, user, refresh]);

  const handleSignOut = async () => {
    if (window.confirm('Are you sure you want to sign out?')) {
      try {
        await signOut();
        navigate('/signin');
      } catch (error) {
        console.error('Sign out error:', error);
        setMessage({ type: 'error', text: 'Failed to sign out. Please try again.' });
      }
    }
  };

  // Loading state
  if (!user) {
    return (
      <div className="bg-white sm:rounded-xl shadow-sm sm:border border-gray-200 h-[calc(100vh-8rem)] sm:h-[calc(100vh-10rem)] min-h-[300px] flex flex-col items-center justify-center p-6">
        <div className="animate-spin rounded-full h-10 w-10 border-b-2 border-aalaw-red mb-4"></div>
        <p className="text-gray-500">Loading profile...</p>
      </div>
    );
  }

  const fullName = user.firstName && user.lastName
    ? `${user.firstName} ${user.lastName}`
    : user.email;

  const initials = user.firstName
    ? `${user.firstName[0]}${user.lastName?.[0] || ''}`
    : user.email[0].toUpperCase();

  return (
    <div className="bg-white sm:rounded-xl shadow-sm sm:border border-gray-200 h-[calc(100vh-8rem)] sm:h-[calc(100vh-10rem)] min-h-[300px] flex flex-col overflow-hidden">
      {/* Header */}
      <div className="flex-shrink-0 border-b border-gray-200 px-4 sm:px-6 py-4">
        <h1 className="text-xl font-bold text-gray-900">Profile</h1>
        <p className="text-sm text-gray-500">Manage your account information</p>
      </div>

      {/* Content */}
      <div className="flex-1 overflow-y-auto overscroll-contain scrollbar-visible" style={{ WebkitOverflowScrolling: 'touch' }}>
        <div className="max-w-lg mx-auto px-4 sm:px-6 py-6 space-y-6">
          {/* Message Banner */}
          {message && (
            <div
              className={`p-4 rounded-lg flex items-center gap-3 ${
                message.type === 'success'
                  ? 'bg-green-50 text-green-800 border border-green-200'
                  : 'bg-red-50 text-red-800 border border-red-200'
              }`}
            >
              {message.type === 'success' ? (
                <svg className="w-5 h-5 flex-shrink-0" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
                </svg>
              ) : (
                <svg className="w-5 h-5 flex-shrink-0" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                </svg>
              )}
              <span className="text-sm flex-1">{message.text}</span>
              <button
                onClick={() => setMessage(null)}
                className="text-current opacity-70 hover:opacity-100"
              >
                <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                </svg>
              </button>
            </div>
          )}

          {/* Profile Header */}
          <div className="flex flex-col items-center text-center py-4">
            <div className="w-20 h-20 rounded-full bg-gradient-to-br from-aalaw-red to-aalaw-red-dark flex items-center justify-center mb-4 shadow-lg">
              <span className="text-2xl font-bold text-white">{initials}</span>
            </div>
            <h2 className="text-xl font-bold text-gray-900 mb-2">{fullName}</h2>
            <span className="inline-flex items-center px-3 py-1 rounded-full text-sm font-medium bg-aalaw-red text-white">
              {formatRole(user.role)}
            </span>
          </div>

          {/* Contact Information */}
          <div className="bg-gray-50 rounded-xl p-4 sm:p-5">
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-base font-semibold text-gray-900">Contact Information</h3>
              {!isEditing && (
                <Button variant="outline" size="sm" onClick={handleStartEdit}>
                  <svg className="w-4 h-4 mr-1.5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15.232 5.232l3.536 3.536m-2.036-5.036a2.5 2.5 0 113.536 3.536L6.5 21.036H3v-3.572L16.732 3.732z" />
                  </svg>
                  Edit
                </Button>
              )}
            </div>

            {isEditing ? (
              <div className="space-y-4">
                {/* Email (read-only) */}
                <div>
                  <label className="block text-sm font-medium text-gray-500 mb-1">Email</label>
                  <Input type="email" value={user.email} disabled className="bg-white" />
                  <p className="text-xs text-gray-400 mt-1">Email cannot be changed</p>
                </div>

                {/* Phone */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Phone Number</label>
                  <Input
                    type="tel"
                    value={phoneNumber}
                    onChange={(e) => setPhoneNumber(e.target.value)}
                    placeholder="(555) 123-4567"
                    className="bg-white"
                  />
                </div>

                {/* Address */}
                <div className="pt-3 border-t border-gray-200">
                  <label className="block text-sm font-medium text-gray-700 mb-3">Address</label>
                  <div className="space-y-3">
                    <Input
                      type="text"
                      value={street1}
                      onChange={(e) => setStreet1(e.target.value)}
                      placeholder="Street Address"
                      className="bg-white"
                    />
                    <Input
                      type="text"
                      value={street2}
                      onChange={(e) => setStreet2(e.target.value)}
                      placeholder="Apt, Suite, Unit (optional)"
                      className="bg-white"
                    />
                    <div className="grid grid-cols-2 sm:grid-cols-3 gap-3">
                      <Input
                        type="text"
                        value={city}
                        onChange={(e) => setCity(e.target.value)}
                        placeholder="City"
                        className="col-span-2 sm:col-span-1 bg-white"
                      />
                      <Input
                        type="text"
                        value={state}
                        onChange={(e) => setState(e.target.value)}
                        placeholder="State"
                        maxLength={2}
                        className="bg-white"
                      />
                      <Input
                        type="text"
                        value={zipCode}
                        onChange={(e) => setZipCode(e.target.value)}
                        placeholder="ZIP Code"
                        className="bg-white"
                      />
                    </div>
                  </div>
                </div>

                {/* Action Buttons */}
                <div className="flex gap-3 pt-3">
                  <Button variant="outline" onClick={handleCancelEdit} disabled={isSaving} className="flex-1">
                    Cancel
                  </Button>
                  <Button
                    onClick={handleSave}
                    disabled={isSaving}
                    className="flex-1 bg-aalaw-red hover:bg-aalaw-red-dark"
                  >
                    {isSaving ? 'Saving...' : 'Save Changes'}
                  </Button>
                </div>
              </div>
            ) : (
              <div className="space-y-1">
                {/* Email */}
                <div className="flex items-start gap-3 py-3 border-b border-gray-200">
                  <svg className="w-5 h-5 text-gray-400 mt-0.5 flex-shrink-0" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 8l7.89 5.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" />
                  </svg>
                  <div className="min-w-0 flex-1">
                    <p className="text-xs text-gray-500">Email</p>
                    <p className="text-sm text-gray-900 truncate">{user.email}</p>
                  </div>
                </div>

                {/* Phone */}
                <div className="flex items-start gap-3 py-3 border-b border-gray-200">
                  <svg className="w-5 h-5 text-gray-400 mt-0.5 flex-shrink-0" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 5a2 2 0 012-2h3.28a1 1 0 01.948.684l1.498 4.493a1 1 0 01-.502 1.21l-2.257 1.13a11.042 11.042 0 005.516 5.516l1.13-2.257a1 1 0 011.21-.502l4.493 1.498a1 1 0 01.684.949V19a2 2 0 01-2 2h-1C9.716 21 3 14.284 3 6V5z" />
                  </svg>
                  <div>
                    <p className="text-xs text-gray-500">Phone</p>
                    <p className="text-sm text-gray-900">{formatPhoneNumber(user.phoneNumber)}</p>
                  </div>
                </div>

                {/* Address */}
                <div className="flex items-start gap-3 py-3">
                  <svg className="w-5 h-5 text-gray-400 mt-0.5 flex-shrink-0" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17.657 16.657L13.414 20.9a1.998 1.998 0 01-2.827 0l-4.244-4.243a8 8 0 1111.314 0z" />
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 11a3 3 0 11-6 0 3 3 0 016 0z" />
                  </svg>
                  <div>
                    <p className="text-xs text-gray-500">Address</p>
                    {user.address ? (
                      <div className="text-sm text-gray-900">
                        <p>{user.address.street1}</p>
                        {user.address.street2 && <p>{user.address.street2}</p>}
                        <p>{user.address.city}, {user.address.state} {user.address.zipCode}</p>
                      </div>
                    ) : (
                      <p className="text-sm text-gray-500 italic">Not provided</p>
                    )}
                  </div>
                </div>
              </div>
            )}
          </div>

          {/* Support */}
          <div className="bg-gray-50 rounded-xl p-4 sm:p-5">
            <h3 className="text-base font-semibold text-gray-900 mb-3">Support</h3>
            <button
              onClick={() => navigate('/client/about')}
              className="w-full flex items-center justify-between py-3 px-3 -mx-3 text-left hover:bg-gray-100 rounded-lg transition-colors"
            >
              <div className="flex items-center gap-3">
                <svg className="w-5 h-5 text-gray-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8.228 9c.549-1.165 2.03-2 3.772-2 2.21 0 4 1.343 4 3 0 1.4-1.278 2.575-3.006 2.907-.542.104-.994.54-.994 1.093m0 3h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                </svg>
                <span className="text-sm text-gray-700">Contact Support</span>
              </div>
              <svg className="w-5 h-5 text-gray-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
              </svg>
            </button>
          </div>

          {/* Sign Out Button */}
          <Button
            variant="outline"
            onClick={handleSignOut}
            className="w-full border-red-200 text-red-600 hover:bg-red-50 hover:border-red-300"
          >
            <svg className="w-5 h-5 mr-2" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 16l4-4m0 0l-4-4m4 4H7m6 4v1a3 3 0 01-3 3H6a3 3 0 01-3-3V7a3 3 0 013-3h4a3 3 0 013 3v1" />
            </svg>
            Sign Out
          </Button>

          {/* App Version */}
          <div className="text-center pb-4">
            <p className="text-xs text-gray-400">AA Law Client Portal v1.0.0</p>
          </div>
        </div>
      </div>
    </div>
  );
}
